import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useTranslation } from "../i18n/LanguageContext";

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

export default function PricingSection() {
  const { t } = useTranslation();
  const p = t.pricing;
  const tiers = p.tiers;
  const projectEngagement = p.projectEngagement;

  return (
    <section
      id="pricing"
      className="py-24 md:py-32 bg-[#0A0A0B]"
      data-testid="pricing-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="mb-16"
        >
          <div className="flex items-center gap-4 mb-6">
            <motion.span
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="inline-block w-10 h-px bg-[#C9A96E]"
              style={{ transformOrigin: "left center" }}
            />
            <span className="fwd-eyebrow">{p.eyebrow}</span>
          </div>
          <h2 className="fwd-heading" data-testid="pricing-headline">
            {p.headline1}
            <br />
            {p.headline2}{" "}
            <span className="fwd-italic-gold">{p.headlineItalic}</span>
          </h2>
          <p className="fwd-body mt-6" style={{ maxWidth: "640px" }}>
            {p.intro}
          </p>
        </motion.div>

        {/* Cards — equal heights, top-aligned content, divider before footer */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8 items-stretch"
        >
          {tiers.map((tier, index) => (
            <motion.div
              key={index}
              variants={item}
              className="relative flex flex-col h-full fwd-card-hover"
              style={{
                background: "#141417",
                border: tier.signature
                  ? "1px solid #C9A96E"
                  : "1px solid #2A2A2E",
                borderRadius: "2px",
              }}
              data-testid={`pricing-tier-${index}`}
            >
              {/* Top content */}
              <div className="p-8 flex flex-col">
                {tier.signature && (
                  <span
                    className="fwd-eyebrow block mb-3"
                    style={{ fontSize: "14px" }}
                    data-testid="signature-badge"
                  >
                    {p.signatureBadge}
                  </span>
                )}

                <span
                  className="mb-3"
                  style={{
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 600,
                    fontSize: "16px",
                    letterSpacing: "0.22em",
                    textTransform: "uppercase",
                    color: "rgba(201, 169, 110, 0.6)",
                  }}
                >
                  {p.tierLabel(tier.tier)}
                </span>

                <h3
                  className="mb-4"
                  style={{
                    fontFamily: "Cormorant Garamond, serif",
                    fontWeight: 500,
                    fontSize: "28px",
                    lineHeight: 1.2,
                    color: "#F5F1E8",
                  }}
                >
                  {tier.name}
                </h3>

                <p className="fwd-body">{tier.description}</p>
              </div>

              {/* Divider between description and feature list */}
              <div style={{ borderTop: "1px solid #2A2A2E" }} />

              {/* Feature checklist — grows to fill */}
              <ul className="px-8 py-6 space-y-3 flex-grow">
                {tier.features.map((feature, fi) => (
                  <li key={fi} className="flex items-start gap-3">
                    <Check
                      size={16}
                      className="mt-1 shrink-0"
                      style={{ color: "#C9A96E" }}
                    />
                    <span className="fwd-body" style={{ fontSize: "17px" }}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Divider before footer */}
              <div style={{ borderTop: "1px solid #2A2A2E" }} />

              {/* Pinned footer line */}
              <div
                className="px-8 py-5"
                style={{
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 600,
                  fontSize: "14px",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: "#C9A96E",
                }}
                data-testid={`pricing-tier-footer-${index}`}
              >
                {tier.footer}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Footnote line */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="fwd-body text-center mb-12"
          style={{ maxWidth: "720px", marginLeft: "auto", marginRight: "auto" }}
          data-testid="pricing-footnote"
        >
          {p.footnote}
        </motion.p>

        {/* Project Engagements card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="p-8 md:p-10 fwd-card-hover"
          style={{
            background: "#141417",
            border: "1px solid #2A2A2E",
            borderRadius: "2px",
          }}
          data-testid="project-engagement-card"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            <div className="flex-1">
              <span className="fwd-eyebrow block mb-3">
                {projectEngagement.eyebrow}
              </span>
              <h3
                className="mb-4"
                style={{
                  fontFamily: "Cormorant Garamond, serif",
                  fontWeight: 500,
                  fontSize: "32px",
                  lineHeight: 1.2,
                  color: "#F5F1E8",
                }}
              >
                {projectEngagement.name}
              </h3>
              <p className="fwd-body" style={{ maxWidth: "640px" }}>
                {projectEngagement.description}
              </p>
            </div>
            <div className="md:text-right md:max-w-[280px]">
              <p
                className="fwd-italic-gold"
                style={{ fontSize: "20px", lineHeight: 1.4 }}
              >
                {projectEngagement.price}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
